/**
 * Human-readable label for an enum listing-field value, via the listing
 * field configuration. Falls back to the raw value when the field or option
 * isn't configured, and to null when there is no value.
 */
export const findEnumLabel = (listingFields, key, value) => {
  if (!value) {
    return null;
  }
  const field = listingFields?.find(f => f.key === key);
  const option = field?.enumOptions?.find(o => o.option === value);
  return option?.label || value;
};

/**
 * The brand as buyers see it. Two rules, both about "Annet" — the dropdown's
 * "none of the above":
 *
 *  - a name typed behind it IS the brand, and beats the word "Annet";
 *  - "Annet" alone is not a brand. It means the seller never told us, and
 *    printing it where a brand goes tells a buyer nothing while looking like
 *    a real answer. Show no brand instead.
 *
 * @param {Array} listingFields - listing field configuration
 * @param {Object} publicData - the listing's publicData
 * @returns {string|null}
 */
export const displayBrand = (listingFields, publicData) => {
  if (publicData?.merke === 'annet') {
    return (publicData?.['merke-annet'] || '').trim() || null;
  }
  return findEnumLabel(listingFields, 'merke', publicData?.merke);
};

/**
 * Name of the most specific category a listing is filed under ("Kåper"
 * rather than "Klær"), looked up in the category tree from the configuration
 * (`config.categoryConfiguration.categories`). Falls back to the raw id when
 * the category isn't configured, and to null when there is none.
 */
export const findCategoryLabel = (categories, publicData) => {
  const id =
    publicData?.categoryLevel3 || publicData?.categoryLevel2 || publicData?.categoryLevel1;
  if (!id) {
    return null;
  }
  const search = nodes => {
    for (const node of nodes || []) {
      if (node.id === id) {
        return node.name;
      }
      const found = search(node.subcategories);
      if (found) {
        return found;
      }
    }
    return null;
  };
  return search(categories) || id;
};

/**
 * The category a listing is filed under that buyers can still browse, at most
 * its second level ("Kjoler", not "Midikjoler"; Guro, 29 Oct): its levels from
 * the top down, stopping at a level that is hidden (`hidden: true`) or no
 * longer in the category tree. Null when even the top level is hidden or
 * unknown.
 *
 * @param {Array} categories - category tree (`config.categoryConfiguration.categories`)
 * @param {Object} publicData - the listing's publicData (categoryLevel1, 2, 3)
 * @returns {{ path: Array<string>, name: string } | null} e.g.
 *   { path: ['klar', 'klar-kjoler'], name: 'Kjoler' }
 */
export const findVisibleCategory = (categories, publicData) => {
  const path = [];
  let found = null;
  let nodes = categories || [];
  for (const level of [1, 2]) {
    const id = publicData?.[`categoryLevel${level}`];
    const node = id ? nodes.find(n => n.id === id) : null;
    if (!node || node.hidden) {
      break;
    }
    path.push(node.id);
    found = { path: [...path], name: node.name };
    nodes = node.subcategories || [];
  }
  return found;
};
